/* eslint-disable quotes */
/* eslint-disable indent */
'use client';

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import Link from 'next/link';
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
} from 'framer-motion';
import {
  Home,
  Key,
  Building,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
} from 'lucide-react';
import { useRouter, usePathname } from 'next/navigation';
import PropertySearch from '../components/search/property-search';
import { useLanguage } from '@/app/context/language-context';
import ImageLoader from '@/app/components/ui/image-loader';
import WhyChooseUsSection from './sections/why-choose-us-section';
import CallToAction from './sections/cta-section';
import TestimonialsSection from './sections/testimonials-section';
import Enhanced3DAgentsSection from './sections/enhanced-3d-agents-section';
import AwardsSection from './sections/awards-section';
import PartnerDevelopersSection from './sections/partner-developers-section';
import NewsBlogSection from './sections/news-blog-section';
import FAQSection from './sections/faq-section';
import NoResultsFound from '@/app/components/search/no-results-found';
import ContactFormModal from '@/app/components/contact/contact-form-modal-pixxi';
import { PropertyService } from '@/app/components/property-service';
import { fadeSlow, fadeUp, luxuryEase } from './animation';
import { useReducedMotionPreference } from './use-reduced-motion-peference';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
};

// Mock data for top sales agents by period
const topSalesData = {
  months: {
    1: [
      {
        id: 1,
        name: 'Sarah Johnson',
        salesVolume: 'AED 75M',
        transactions: 12,
        rank: 1,
        image: '/placeholder.svg?height=200&width=200',
      },
      {
        id: 3,
        name: 'Jessica Chen',
        salesVolume: 'AED 68M',
        transactions: 9,
        rank: 2,
        image: '/placeholder.svg?height=200&width=200',
      },
      {
        id: 2,
        name: 'Mohammed Al Farsi',
        salesVolume: 'AED 52M',
        transactions: 7,
        rank: 3,
        image: '/placeholder.svg?height=200&width=200',
      },
    ],
    2: [
      {
        id: 3,
        name: 'Jessica Chen',
        salesVolume: 'AED 82M',
        transactions: 11,
        rank: 1,
        image: '/placeholder.svg?height=200&width=200',
      },
      {
        id: 1,
        name: 'Sarah Johnson',
        salesVolume: 'AED 70M',
        transactions: 10,
        rank: 2,
        image: '/placeholder.svg?height=200&width=200',
      },
      {
        id: 4,
        name: 'Ahmed Hassan',
        salesVolume: 'AED 45M',
        transactions: 6,
        rank: 3,
        image: '/placeholder.svg?height=200&width=200',
      },
    ],
    // Add more months as needed
  },
  quarters: {
    1: [
      {
        id: 1,
        name: 'Sarah Johnson',
        salesVolume: 'AED 210M',
        transactions: 32,
        rank: 1,
        image: '/placeholder.svg?height=200&width=200',
      },
      {
        id: 3,
        name: 'Jessica Chen',
        salesVolume: 'AED 195M',
        transactions: 28,
        rank: 2,
        image: '/placeholder.svg?height=200&width=200',
      },
      {
        id: 2,
        name: 'Mohammed Al Farsi',
        salesVolume: 'AED 150M',
        transactions: 22,
        rank: 3,
        image: '/placeholder.svg?height=200&width=200',
      },
    ],
    2: [
      {
        id: 3,
        name: 'Jessica Chen',
        salesVolume: 'AED 230M',
        transactions: 35,
        rank: 1,
        image: '/placeholder.svg?height=200&width=200',
      },
      {
        id: 1,
        name: 'Sarah Johnson',
        salesVolume: 'AED 205M',
        transactions: 30,
        rank: 2,
        image: '/placeholder.svg?height=200&width=200',
      },
      {
        id: 4,
        name: 'Ahmed Hassan',
        salesVolume: 'AED 180M',
        transactions: 25,
        rank: 3,
        image: '/placeholder.svg?height=200&width=200',
      },
    ],
    // Add more quarters as needed
  },
  years: {
    2023: [
      {
        id: 3,
        name: 'Jessica Chen',
        salesVolume: 'AED 850M',
        transactions: 120,
        rank: 1,
        image: '/placeholder.svg?height=200&width=200',
      },
      {
        id: 1,
        name: 'Sarah Johnson',
        salesVolume: 'AED 780M',
        transactions: 105,
        rank: 2,
        image: '/placeholder.svg?height=200&width=200',
      },
      {
        id: 2,
        name: 'Mohammed Al Farsi',
        salesVolume: 'AED 620M',
        transactions: 85,
        rank: 3,
        image: '/placeholder.svg?height=200&width=200',
      },
    ],
    2024: [
      {
        id: 1,
        name: 'Sarah Johnson',
        salesVolume: 'AED 320M',
        transactions: 45,
        rank: 1,
        image: '/placeholder.svg?height=200&width=200',
      },
      {
        id: 3,
        name: 'Jessica Chen',
        salesVolume: 'AED 290M',
        transactions: 40,
        rank: 2,
        image: '/placeholder.svg?height=200&width=200',
      },
      {
        id: 4,
        name: 'Ahmed Hassan',
        salesVolume: 'AED 210M',
        transactions: 30,
        rank: 3,
        image: '/placeholder.svg?height=200&width=200',
      },
    ],
  },
};

export default function LandingPage() {
  const { t } = useLanguage();
  const router = useRouter();

  const [index, setIndex] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [periodType, setPeriodType] = useState('months'); // months, quarters, years
  const [selectedPeriod, setSelectedPeriod] = useState(1); // Default to January or Q1 or 2024
  const [cardsPerView, setCardsPerView] = useState(3);
  const [featuredProperties, setProperties] = useState([]);
  const sliderRef = useRef(null);
  const [direction, setDirection] = useState(1); // 1 for next, -1 for prev
  const [videoLoaded, setVideoLoaded] = useState(false);

  const [startIndex, setStartIndex] = useState(0);
  const [useVideo, setUseVideo] = useState(true); // Default to video background
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const heroRef = useRef(null);
  const searchRef = useRef(null);

  const [loading, setLoading] = useState(true);

  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);

  const reducedMotion = useReducedMotionPreference();

  const fetchProperties = useCallback(
    async (retries = 3) => {
      try {
        setLoading(true);
        const data = await PropertyService.getPFProperties('buy');
        setProperties(data);
      } catch (error) {
        console.error('Error fetching properties:', error);
        if (retries > 0) {
          setTimeout(() => fetchProperties(retries - 1), 2000);
        } else {
          console.error('Failed to fetch properties after retries.');
        }
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchProperties(); // initial call with default retry
  }, [fetchProperties]);

  useEffect(() => {
    if (featuredProperties && featuredProperties.length > 0) {
      setLoading(false);
    }
  }, [featuredProperties]);

  // Get current year
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // JavaScript months are 0-indexed

  const totalSlides = Math.ceil(featuredProperties.length / cardsPerView);

  // Set default year to current year
  useEffect(() => {
    if (periodType === 'years') {
      setSelectedPeriod(currentYear);
    } else if (periodType === 'months') {
      setSelectedPeriod(currentMonth);
    }
  }, [periodType, currentYear, currentMonth]);

  // Scroll animations
  const { scrollY } = useScroll();

  // Background height animation
  const backgroundHeight = useTransform(
    scrollY,
    [0, 400, 800],
    ['100vh', '100vh', '50vh'],
  );

  // Title and subtitle move up slightly but remain visible
  const heroY = useTransform(scrollY, [0, 300], [0, -50]);

  // Title and subtitle animations
  const titleOpacity = useTransform(scrollY, [0, 200], [1, 0]);
  const titleY = useTransform(scrollY, [0, 200], [0, -100]);

  // Search component animations
  const searchOpacity = useTransform(scrollY, [100, 300], [0, 1]);
  const searchScale = useTransform(
    scrollY,
    [100, 300, 600, 800],
    [0.8, 1, 1, 0.9],
  );
  const searchY = useTransform(scrollY, [100, 300, 600, 800], [50, 0, 0, -50]);

  // Update visible cards based on screen size
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);

      if (mobile) {
        setCardsPerView(1);
      } else if (window.innerWidth < 1024) {
        setCardsPerView(2);
      } else {
        setCardsPerView(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const nextSlide = () => {
    setDirection(1);
    const maxIndex = Math.max(0, featuredProperties.length - cardsPerView);
    const newIndex = currentIndex >= maxIndex ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    scrollToCard(newIndex);
  };

  const prevSlide = () => {
    setDirection(-1);
    const maxIndex = Math.max(0, featuredProperties.length - cardsPerView);
    const newIndex = currentIndex <= 0 ? maxIndex : currentIndex - 1;
    setCurrentIndex(newIndex);
    scrollToCard(newIndex);
  };

  const scrollToCard = (index) => {
    if (sliderRef.current && isMobile) {
      const cardWidth =
        sliderRef.current.scrollWidth / featuredProperties.length;
      sliderRef.current.scrollTo({
        left: index * cardWidth,
        behavior: 'smooth',
      });
    }
  };

  // Get month name from number
  const getMonthName = (monthNum) => {
    const months = [
      t('january'),
      t('february'),
      t('march'),
      t('april'),
      t('may'),
      t('june'),
      t('july'),
      t('august'),
      t('september'),
      t('october'),
      t('november'),
      t('december'),
    ];
    return months[monthNum - 1];
  };

  // Get quarter name from number
  const getQuarterName = (quarterNum) => {
    const quarters = [t('q1'), t('q2'), t('q3'), t('q4')];
    return quarters[quarterNum - 1];
  };

  // Get top sales data based on selected period
  const getTopSalesData = () => {
    // First check if the year exists in our data
    if (!topSalesData.years[selectedYear]) {
      return [];
    }

    // If period type is months, get monthly data
    if (periodType === 'months' && topSalesData.months[selectedPeriod]) {
      return topSalesData.months[selectedPeriod];
    }
    // If period type is quarters, get quarterly data
    else if (
      periodType === 'quarters' &&
      topSalesData.quarters[selectedPeriod]
    ) {
      return topSalesData.quarters[selectedPeriod];
    }

    // Default to yearly data if specific period not found
    return topSalesData.years[selectedYear];
  };

  // Add this effect to reset the period when period type changes
  useEffect(() => {
    setSelectedPeriod(1); // Reset to first period when type changes
  }, [periodType]);

  const visibleProperties = useMemo(() => {
    return featuredProperties.slice(currentIndex, currentIndex + cardsPerView);
  }, [featuredProperties, currentIndex, cardsPerView]);

  // Mouse drag handlers for slider (mobile only)
  const handleMouseDown = (e) => {
    if (!sliderRef.current || !isMobile) return;
    setIsDragging(true);
    setStartX(e.pageX - sliderRef.current.offsetLeft);
    setScrollLeft(sliderRef.current.scrollLeft);
    sliderRef.current.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !sliderRef.current || !isMobile) return;
    e.preventDefault();
    const x = e.pageX - sliderRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    sliderRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    if (!isMobile) return;
    setIsDragging(false);
    if (sliderRef.current) {
      sliderRef.current.style.cursor = 'grab';

      // Calculate which card is most visible and set currentIndex accordingly
      if (sliderRef.current.scrollLeft > 0) {
        const cardWidth = sliderRef.current.offsetWidth;
        const newIndex = Math.round(sliderRef.current.scrollLeft / cardWidth);
        setCurrentIndex(
          Math.min(newIndex, featuredProperties.length - cardsPerView),
        );
      }
    }
  };

  const handleScroll = () => {
    if (!isMobile || !sliderRef.current) return;
    const cardWidth = sliderRef.current.offsetWidth;
    const newIndex = Math.round(sliderRef.current.scrollLeft / cardWidth);
    if (newIndex !== currentIndex) {
      setCurrentIndex(
        Math.min(newIndex, featuredProperties.length - cardsPerView),
      );
    }
  };

  // Toggle between video and image background
  const toggleBackgroundType = () => {
    setUseVideo(!useVideo);
  };

  const offeringType = (type) => {
    const types = { CS: 'Buy', RS: 'Buy', RR: 'Rent', CR: 'Rent' };
    return types[type.trim().toUpperCase()] || 'N/A';
  };

  const offPlanType = (type) => {
    if (type === 'off plan') {
      return 'Off-Plan-Secondary';
    } else if (type === 'off_plan_primary') {
      return 'Off-Plan-Primary';
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoadVideo(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 },
    );

    if (heroRef.current) observer.observe(heroRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div className='bg-gradient-to-b from-white to-gray-100 dark:from-gray-900 dark:to-black'>
      {/* Hero Section with Background */}

      <motion.section
        className='relative h-[112vh] sm:h-[120vh] lg:h-[130vh] bg-black/80'
        ref={heroRef}
      >
        {/* Background Media */}
        <div className='absolute inset-0 w-full h-full'>
          {shouldLoadVideo && !videoLoaded && !reducedMotion && (
            <div className='absolute inset-0 flex items-center justify-center bg-black z-20'>
              <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-white/70' />
            </div>
          )}

          {!reducedMotion && shouldLoadVideo && (
            <video
              // className='absolute inset-0 w-full h-full object-cover z-10'
              className='absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 z-10'
              autoPlay
              loop
              muted
              playsInline
              preload='none'
              poster='https://images.x-space.ae/assets/images/website_image/new_landing_page_desktop.webp'
              onCanPlay={() => setVideoLoaded(true)}
              style={{ opacity: videoLoaded ? 1 : 0 }}
            >
              <source
                src='https://images.x-space.ae/assets/videos/new_landing_page_video_6.avi'
                type='video/av1'
              />
              <source
                src='https://images.x-space.ae/assets/videos/new_landing_page_video_6.webm'
                type='video/webm'
              />
              <source
                src='https://images.x-space.ae/assets/videos/new_landing_page_video_6.mp4'
                type='video/mp4'
              />
              Your browser does not support the video tag
            </video>
          )}

          {reducedMotion && (
            <div
              className='absolute inset-0 bg-cover bg-center'
              style={{
                backgroundImage:
                  "url('https://images.x-space.ae/assets/images/website_image/new_landing_page_desktop.webp')",
              }}
            />
          )}

          <div className='absolute inset-0 bg-gradient-to-b from-black/10 via-black/10 to-black/45' />

          <div className='absolute inset-0 shadow-[inset_0_0_220px_rgba(0,0,0,0.85)]' />
        </div>

        <div className='relative h-full flex flex-col justify-center items-center'>
          {/* Hero Content */}
          <motion.div
            className='relative max-w-4xl mx-auto text-center px-4 z-10'
            variants={fadeSlow}
            initial='initial'
            animate='animate'
            transition={{ duration: 1.4, ease: luxuryEase }}
            style={{
              opacity: titleOpacity,
              y: titleY,
            }}
          >
            <motion.h1
              // className='text-3xl sm:text-3xl lg:text-6xl font-semibold mb-8 text-gradient'
              className=' text-5xl sm:text-4xl lg:text-6xl font-semibold mb-8 text-white drop-shadow-[0_4px_25px_rgba(0,0,0,0.65)]'
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
            >
              {t('heroTitle') || 'Find Your Dream Home in Dubai'}
            </motion.h1>

            <motion.p
              // className='text-lg sm:text-xl lg:text-2xl text-black/100 mb-8 max-w-3xl mx-auto font-bold bg-secondarycolor/90 rounded-xl'
              className='text-xl sm:text-lg lg:text-2xl text-white mb-8 max-w-3xl mx-auto font-extrabold backdrop-blur-lg bg-white/10 border border-white/20 rounded-2xl px-4 py-3'
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut', delay: 0.4 }}
            >
              {t('heroSubtitle') ||
                'Discover luxury properties in the most prestigious locations'}
            </motion.p>

            <motion.div
              className='flex justify-center mb-8'
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut', delay: 0.6 }}
            >
              <motion.button
                // className='px-8 py-4 bg-primarycolor/90 hover:bg-secondarycolor/85 text-white hover:text-black rounded-full font-medium text-lg transition-colors shadow-lg'
                className='px-8 py-4 rounded-full font-bold text-lg text-black dark:text-gray-200 bg-white/90 dark:bg-gray-500 hover:bg-secondarycolor/85 dark:hover:bg-primarycolor/85 shadow-[0_10px_40px_rgba(0,0,0,0.35)] backdrop-blur-sm'
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  window.scrollTo({
                    top: 300,
                    behavior: 'smooth',
                  });
                }}
              >
                Explore Properties
              </motion.button>
            </motion.div>
          </motion.div>

          <motion.div
            ref={searchRef}
            // className='absolute left-0 right-0 mx-auto w-full px-4 z-20 mt-20 mb-20'
            className='absolute mx-auto w-full max-w-6xl px-0 z-20 mt-20 mb-20'
            style={{
              opacity: searchOpacity,
              scale: searchScale,
              y: searchY,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            <div
              className='
              lg:backdrop-blur-xl
              rounded-2xl
              lg:shadow-[0_35px_90px_rgba(0,0,0,0.25)]
              p-3 lg:p-6
            '
            >
              <PropertySearch
                NoResultsComponent={(props) => (
                  <NoResultsFound
                    onContactClick={() => setIsContactModalOpen(true)}
                    {...props}
                  />
                )}
              />
            </div>
          </motion.div>

          <div className='absolute bottom-6 text-white/70 text-sm tracking-wide animate-bounce font-bold'>
            Scroll
          </div>
        </div>
      </motion.section>

      {/* Spacer to ensure content below doesn't overlap with hero section */}
      {/* <div className='h-32 bg-white dark:bg-gray-900'></div> */}
      {/* Featured Properties Section (Slider) */}
      <motion.section
        className='relative py-20 px-4 bg-gray-50 dark:bg-gray-900 z-10'
        initial='hidden'
        whileInView='visible'
        viewport={{ once: true, margin: '-100px' }}
        variants={staggerContainer}
      >
        <div className='max-w-7xl mx-auto'>
          <motion.div className='text-center mb-12' variants={fadeIn}>
            <h2 className='text-3xl font-bold text-gray-900 dark:text-white mb-4'>
              {t('featuredProperties')}
            </h2>
            <p className='text-gray-600 dark:text-gray-400 max-w-2xl mx-auto'>
              {t('featuredDescription')}
            </p>
          </motion.div>

          {/* Show Skeleton While Loading */}
          {loading ? (
            isMobile ? (
              <div className='flex overflow-x-auto hide-scrollbar snap-x snap-mandatory space-x-4'>
                {Array.from({ length: 4 }).map((_, index) => (
                  <div
                    key={index}
                    className='min-w-[85vw] sm:min-w-[350px] bg-white dark:bg-gray-800 rounded-xl shadow-lg snap-center animate-pulse'
                  >
                    <div className='relative h-64 bg-gray-200 dark:bg-gray-700 rounded-t-xl' />
                    <div className='p-6 space-y-4'>
                      <div className='h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4' />
                      <div className='h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3' />
                      <div className='flex justify-between mt-3'>
                        <div className='h-4 bg-gray-200 dark:bg-gray-700 rounded w-16' />
                        <div className='h-4 bg-gray-200 dark:bg-gray-700 rounded w-16' />
                        <div className='h-4 bg-gray-200 dark:bg-gray-700 rounded w-16' />
                      </div>
                      <div className='flex justify-between items-center mt-4'>
                        <div className='h-6 bg-gray-200 dark:bg-gray-700 rounded w-24' />
                        <div className='h-9 w-24 bg-gray-200 dark:bg-gray-700 rounded-md' />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 my-7 mx-5'>
                {Array.from({ length: 3 }).map((_, index) => (
                  <div
                    key={index}
                    className='bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden animate-pulse'
                  >
                    <div className='relative h-64 bg-gray-200 dark:bg-gray-700' />
                    <div className='p-6 space-y-4'>
                      <div className='h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4' />
                      <div className='flex items-center space-x-2'>
                        <div className='h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded-full' />
                        <div className='h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3' />
                      </div>
                      <div className='flex justify-between'>
                        <div className='flex items-center space-x-2'>
                          <div className='h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded-full' />
                          <div className='h-4 bg-gray-200 dark:bg-gray-700 rounded w-12' />
                        </div>
                        <div className='flex items-center space-x-2'>
                          <div className='h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded-full' />
                          <div className='h-4 bg-gray-200 dark:bg-gray-700 rounded w-12' />
                        </div>
                        <div className='flex items-center space-x-2'>
                          <div className='h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded-full' />
                          <div className='h-4 bg-gray-200 dark:bg-gray-700 rounded w-16' />
                        </div>
                      </div>
                      <div className='flex justify-between items-center mt-4'>
                        <div className='h-6 bg-gray-200 dark:bg-gray-700 rounded w-24' />
                        <div className='h-9 w-24 bg-gray-200 dark:bg-gray-700 rounded-md' />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <>
              {/* --- Original Featured Properties Grid / Slider (your same code) --- */}
              <div
                className={`mb-6 space-x-2 ${isMobile ? 'hidden' : 'flex justify-end'}`}
              >
                <button
                  onClick={prevSlide}
                  className='p-2 rounded-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-primarycolor dark:hover:bg-primarycolor hover:text-white transition-colors shadow-md'
                  aria-label='Previous slide'
                  // disabled={
                  //   currentIndex + cardsPerView >= featuredProperties.length
                  // }
                  disabled={currentIndex === 0}
                >
                  <ChevronLeft className='w-5 h-5' />
                </button>
                <button
                  onClick={nextSlide}
                  className='p-2 rounded-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-primarycolor dark:hover:bg-primarycolor hover:text-white transition-colors shadow-md'
                  aria-label='Next slide'
                  disabled={
                    currentIndex + cardsPerView >= featuredProperties.length
                  }
                >
                  <ChevronRight className='w-5 h-5' />
                </button>
              </div>

              {/* Keep your entire mobile/desktop render logic here as is */}
              {/* (Your map of featuredProperties and visibleProperties) */}
              {isMobile ? (
                <div
                  // className='flex overflow-x-auto snap-x snap-mandatory hide-scrollbar'
                  className={`flex overflow-x-auto ${
                    isMobile ? 'snap-x snap-mandatory' : ''
                  } hide-scrollbar`}
                  style={{ scrollBehavior: 'smooth' }}
                >
                  {featuredProperties.map((property, index) => (
                    <motion.div
                      key={`${index}-${currentIndex}`}
                      className='bg-white dark:bg-gray-800 rounded-xl shadow-lg group relative min-w-[85vw] sm:min-w-[350px] snap-center px-2 mb-14'
                    >
                      <div className='relative h-64 rounded-xl overflow-hidden'>
                        <ImageLoader
                          src={property.photo_url || '/placeholder.svg'}
                          alt={property.title_en[0]}
                          fill
                          loaderType='hash'
                          objectFit='cover'
                          priority={index === 0}
                          className='rounded-xl'
                          sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 392px'
                        />
                        <div className='absolute top-4 right-4 bg-primarycolor text-white px-3 py-1 rounded-full text-sm font-medium'>
                          For{' '}
                          {offPlanType(property?.completion_status?.[0]) ||
                            offeringType(property.offering_type?.[0])}
                        </div>
                      </div>

                      <div className='p-6'>
                        <h3
                          title={property.title_en[0]}
                          className='text-xl font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2 break-words'
                        >
                          {property.title_en[0]}
                        </h3>
                        <div className='flex items-center text-gray-500 dark:text-gray-400 mb-4'>
                          <TrendingUp className='h-5 w-5 mr-1' />
                          <span>{property.community?.[0] || property.community}</span>
                        </div>

                        <div className='flex justify-between mb-4'>
                          <div className='flex items-center'>
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              className='h-5 w-5 text-gray-500 dark:text-gray-400 mr-2'
                              fill='none'
                              viewBox='0 0 24 24'
                              stroke='currentColor'
                            >
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6'
                              />
                            </svg>
                            <span className='text-gray-600 dark:text-gray-300'>
                              {property.bedroom} Beds
                            </span>
                          </div>
                          <div className='flex items-center'>
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              className='h-5 w-5 text-gray-500 dark:text-gray-400 mr-2'
                              fill='none'
                              viewBox='0 0 24 24'
                              stroke='currentColor'
                            >
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
                              />
                            </svg>
                            <span className='text-gray-600 dark:text-gray-300'>
                              {property.bathroom} Baths
                            </span>
                          </div>
                          <div className='flex items-center'>
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              className='h-5 w-5 text-gray-500 dark:text-gray-400 mr-2'
                              fill='none'
                              viewBox='0 0 24 24'
                              stroke='currentColor'
                            >
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5'
                              />
                            </svg>
                            <span className='text-gray-600 dark:text-gray-300'>
                              {property.size} sqft
                            </span>
                          </div>
                        </div>
                        <div className='flex justify-between items-center'>
                          <span className='text-2xl font-bold text-primarycolor dark:text-secondarycolor'>
                            AED {Number(property.price[0]).toLocaleString()}
                          </span>
                          {/* <motion.button
                          className='px-4 py-2 bg-primarycolor text-white rounded-md hover:bg-secondarycolor hover:text-black transition-colors'
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          {t('explore')}
                        </motion.button> */}

                          <Link
                            rel='preload'
                            // href={`/properties/${
                            //   offPlanType(property?.completion_status?.[0])
                            //     ? 'off-plan'
                            //     : offeringType(property.offering_type?.[0])
                            //         ?.toLowerCase()
                            //         .replace(/_/g, '-')
                            // }/${String(property.slug)
                            //   .trim()
                            //   .toLowerCase()
                            //   .replace(/^-+|-+$/g, '')}`}
                            href={`/properties/${
                              offPlanType(property?.completion_status?.[0])
                                ? 'off-plan'
                                : offeringType(property.offering_type?.[0])
                                    ?.toLowerCase()
                                    .replace(/_/g, '-')
                            }/${property.slug}`}
                            whilehover={{ scale: 1.05 }}
                            whiletap={{ scale: 0.95 }}
                          >
                            <span className='px-4 py-2 bg-primarycolor text-white rounded-md hover:bg-secondarycolor hover:text-black transition-colors'>
                              {t('explore')}
                            </span>
                          </Link>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                // Desktop view - controlled by navigation
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 my-7 mx-5'>
                  <AnimatePresence initial={false} mode='popLayout'>
                    {visibleProperties.map((property, index) => (
                      <motion.div
                        key={`${index}-${currentIndex}`}
                        className='bg-white dark:bg-gray-800 rounded-xl shadow-lg group relative'
                        initial={{ x: direction * 300, opacity: 0 }}
                        animate={{
                          opacity: 1,
                          x: 0,
                          transition: {
                            type: 'spring',
                            stiffness: 300,
                            damping: 30,
                            delay: index * 0.1,
                          },
                        }}
                        exit={{
                          x: direction * -300,
                          opacity: 0,
                          transition: { duration: 0.3 },
                        }}
                        whilehover={{
                          opacity: 1,
                          y: -10,
                          boxShadow: '0px 15px 25px rgba(0, 0, 0, 0.15)',
                        }}
                      >
                        <div className='relative h-64 rounded-xl overflow-hidden'>
                          <ImageLoader
                            src={property.photo_url || '/placeholder.svg'}
                            alt={property.title_en[0]}
                            fill
                            loaderType='hash'
                            objectFit='cover'
                            priority={index === 0}
                            className='rounded-xl'
                            sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 392px'
                          />
                          <div className='absolute top-4 right-4 bg-primarycolor text-white px-3 py-1 rounded-full text-sm font-medium'>
                            For{' '}
                            {offPlanType(property?.completion_status?.[0]) ||
                              offeringType(property.offering_type?.[0])}
                          </div>
                        </div>

                        <div className='p-6'>
                          {/* <h3 className='text-xl font-semibold text-gray-900 dark:text-white mb-2'>
                          {property.title_en[0]}
                        </h3> */}
                          <h3
                            title={property.title_en[0]}
                            className='text-xl font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2 break-words'
                          >
                            {property.title_en[0]}
                          </h3>
                          <div className='flex items-center text-gray-500 dark:text-gray-400 mb-4'>
                            <TrendingUp className='h-5 w-5 mr-1' />
                            <span>{property.community?.[0] || property.community}</span>
                          </div>

                          <div className='flex justify-between mb-4'>
                            <div className='flex items-center'>
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                className='h-5 w-5 text-gray-500 dark:text-gray-400 mr-2'
                                fill='none'
                                viewBox='0 0 24 24'
                                stroke='currentColor'
                              >
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth={2}
                                  d='M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6'
                                />
                              </svg>
                              <span className='text-gray-600 dark:text-gray-300'>
                                {property.bedroom} Beds
                              </span>
                            </div>
                            <div className='flex items-center'>
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                className='h-5 w-5 text-gray-500 dark:text-gray-400 mr-2'
                                fill='none'
                                viewBox='0 0 24 24'
                                stroke='currentColor'
                              >
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth={2}
                                  d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
                                />
                              </svg>
                              <span className='text-gray-600 dark:text-gray-300'>
                                {property.bathroom} Baths
                              </span>
                            </div>
                            <div className='flex items-center'>
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                className='h-5 w-5 text-gray-500 dark:text-gray-400 mr-2'
                                fill='none'
                                viewBox='0 0 24 24'
                                stroke='currentColor'
                              >
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth={2}
                                  d='M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5'
                                />
                              </svg>
                              <span className='text-gray-600 dark:text-gray-300'>
                                {property.size} sqft
                              </span>
                            </div>
                          </div>
                          <div className='flex justify-between items-center'>
                            <span className='text-2xl font-bold text-primarycolor dark:text-secondarycolor'>
                              AED {Number(property.price[0]).toLocaleString()}
                            </span>
                            {/* <motion.button
                            className='px-4 py-2 bg-primarycolor text-white rounded-md hover:bg-secondarycolor hover:text-black transition-colors'
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            {t('explore')}
                          </motion.button> */}
                            <Link
                              rel='preload'
                              href={`/properties/${
                                offPlanType(property?.completion_status?.[0])
                                  ? 'off-plan'
                                  : offeringType(property.offering_type?.[0])
                                      ?.toLowerCase()
                                      .replace(/_/g, '-')
                              }/${property.slug}`}
                              whilehover={{ scale: 1.05 }}
                              whiletap={{ scale: 0.95 }}
                            >
                              <span className='px-4 py-2 bg-primarycolor text-white rounded-md hover:bg-secondarycolor hover:text-black transition-colors'>
                                {t('explore')}
                              </span>
                            </Link>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </>
          )}

          {/* Mobile scroll indicator */}
          {!loading && (
            <div
              className={`mb-4 text-center text-sm text-gray-500 dark:text-gray-400 ${
                isMobile ? 'block' : 'hidden'
              }`}
            >
              <span className='inline-flex items-center'>
                <ChevronLeft className='w-4 h-4 mr-1' />
                Swipe to explore
                <ChevronRight className='w-4 h-4 ml-1' />
              </span>
            </div>
          )}

          {!loading && (
            <motion.div className='text-center mt-12' variants={fadeIn}>
              <motion.button
                className='px-6 py-3 bg-white dark:bg-gray-800 text-primarycolor dark:text-secondarycolor border-2 border-primarycolor dark:border-secondarycolor rounded-md font-medium hover:bg-primarycolor hover:text-white dark:hover:bg-secondarycolor dark:hover:text-black transition-colors'
                whilehover={{ scale: 1.05 }}
                whiletap={{ scale: 0.95 }}
                onClick={() => router.push('/properties/buy')}
              >
                {t('viewAllProperties')}
              </motion.button>
            </motion.div>
          )}
        </div>
      </motion.section>

      {/* Enhanced 3D Agents Section - New section with 3D effects */}
      <Enhanced3DAgentsSection />

      {/* Featured Categories */}
      <motion.section
        className='py-10 px-4 bg-white dark:bg-gray-900'
        initial='hidden'
        whileInView='visible'
        viewport={{ once: true, margin: '-100px' }}
        variants={staggerContainer}
      >
        <div className='max-w-7xl mx-auto'>
          <motion.h2
            className='text-3xl font-bold text-center mb-12 text-gray-900 dark:text-white'
            variants={fadeIn}
          >
            {t('exploreServices')}
          </motion.h2>

          <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
            {[
              {
                title: t('buyProperties'),
                text: t('buyDescription'),
                icon: <Home className='w-6 h-6 text-primarycolor' />,
                href: '/properties/buy',
              },
              {
                title: t('rentProperties'),
                text: t('rentDescription'),
                icon: <Key className='w-6 h-6 text-primarycolor' />,
                href: '/properties/rent',
              },
              {
                title: t('offPlanProjects'),
                text: t('offPlanDescription'),
                icon: <Building className='w-6 h-6 text-primarycolor' />,
                href: '/properties/off-plan',
              },
              // {
              //   title: t('buyProperties'),
              //   text: t('buyDescription'),
              //   icon: <Home className='w-6 h-6 text-primarycolor' />,
              // },
              // {
              //   title: t('rentProperties'),
              //   text: t('rentDescription'),
              //   icon: <Key className='w-6 h-6 text-primarycolor' />,
              // },
              // {
              //   title: t('offPlanProjects'),
              //   text: t('offPlanDescription'),
              //   icon: <Building className='w-6 h-6 text-primarycolor' />,
              // },
            ].map((item, index) => (
              <motion.div
                key={index}
                className='bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl border-b-4 border-transparent hover:border-primarycolor'
                variants={cardVariants}
                whilehover={{
                  scale: 1.03,
                  transition: { duration: 0.2 },
                }}
              >
                <div className='flex items-center justify-between mb-4'>
                  <h2 className='text-xl font-semibold text-gray-900 dark:text-white'>
                    {item.title}
                  </h2>
                  {item.icon}
                </div>
                <p className='text-gray-600 dark:text-gray-300'>{item.text}</p>
                <motion.button
                  className='mt-4 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-md text-primarycolor dark:text-secondarycolor font-medium hover:bg-primarycolor hover:text-white dark:hover:bg-secondarycolor dark:hover:text-black transition-colors'
                  whilehover={{ scale: 1.05 }}
                  whiletap={{ scale: 0.95 }}
                  onClick={() => router.push(`${item?.href}`)}
                >
                  {t('explore')}
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Why Choose Us Section */}
      <WhyChooseUsSection />

      {/* Awards Section - New section */}
      <AwardsSection />

      {/* Partner Developers Section - New section */}
      <PartnerDevelopersSection />

      {/* News & Blog Section - New section with split layout */}
      <NewsBlogSection />

      {/* CTA Section */}
      <FAQSection />

      {/* CTA Section */}
      <CallToAction />

      {/* TODO: Testimonials Section */}
      <TestimonialsSection />

      {/* Contact Form Modal */}
      <ContactFormModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </div>
  );
}
